<template>
  <ExpansionPanel v-model="isExpanded">
    <template #activator>
      <div class="flex w-full items-center hover:bg-light-neutral-200 pr-6">
        <SortableItem
          class="w-full"
          drag-handle-classes="invisible group-hover:visible"
          :hide-drag-handle="false"
          @dragstart="onDragStart"
          @dragend="onDragEnd"
        >
          <div class="flex justify-between items-center w-full hover:bg-gray-50">
            <RouterLink
              :to="latestReview?.id ? `/reviews/${latestReview.id}` : ''"
              class="py-3 px-2 flex gap-2 h-full w-full items-center group/review"
            >
              <DocumentTextIcon
                class="size-5 text-dark-neutral-500 group-hover/review:text-primary-500"
              />

              <div class="flex items-center gap-2 truncate">
                <span class="text-sm leading-5 text-dark-neutral-500 font-medium truncate">
                  {{ reviewSeries.name }}
                </span>
              </div>
            </RouterLink>
            <div class="flex items-center gap-2.5">
              <button
                :class="[
                  'px-4 py-1 text-sm font-medium rounded-full shadow-sm transition whitespace-nowrap',
                  isExpanded
                    ? 'bg-primary-500 text-white'
                    : 'bg-primary-50 text-primary-500 border-1 border-primary-500',
                ]"
                @click.stop="isExpanded = !isExpanded"
              >
                {{ reviewSeries.reviews.length }} version{{
                  reviewSeries.reviews.length > 1 ? "s" : ""
                }}
              </button>

              <div class="flex items-center gap-2">
                <ReviewItemActions :review-series="reviewSeries" :project="project" />
              </div>
            </div>
          </div>
        </SortableItem>
      </div>
    </template>
    <template #default>
      <div v-if="reviewSeries.reviews?.length" class="relative flex flex-col pl-27">
        <div class="absolute h-full w-px bg-mid-neutral-50" />

        <div
          v-for="review in [...reviewSeries.reviews].sort(
            (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
          )"
          :key="'child-' + review.id"
          class="text-sm flex items-center rounded-md pl-2 hover:bg-gray-50 group/row"
        >
          <ReviewVersion :review="review" />
        </div>
      </div>
    </template>
  </ExpansionPanel>
</template>

<script lang="ts" setup>
import { computed } from "vue";
import { Project } from "@core/domain/models/project.model";
import { DocumentTextIcon } from "@heroicons/vue/24/outline";
import ReviewItemActions from "./ReviewSettings/Index.vue";
import SortableItem from "@app/components/Global/Sortable/SortableItem.vue";
import ExpansionPanel from "@app/components/Global/ExpansionPanel.vue";
import ReviewVersion from "./ReviewVersion.vue";
import { useStorage } from "@vueuse/core";

const props = defineProps<{
  reviewSeries: {
    id: string;
    name: string;
    order: string | null;
    reviews: {
      id: number;
      createdAt: Date;
      isLocked: boolean;
      name: string;
    }[];
  };
  project: Project;
  onDragStart: () => void;
  onDragEnd: () => void;
}>();

const openReviewId = useStorage<string | null>("openReviewSeriesId", null, localStorage);

const isExpanded = computed({
  get: () => openReviewId.value === props.reviewSeries.id,
  set: (val: boolean) => {
    openReviewId.value = val ? props.reviewSeries.id : null;
  },
});

const latestReview = computed(() => {
  return props.reviewSeries.reviews.reduce(
    (latest, current) =>
      latest && new Date(current.createdAt) < new Date(latest.createdAt) ? latest : current,
    undefined as { id: number; createdAt: Date } | undefined,
  );
});
</script>
